import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useTranslation } from 'react-i18next'

/**
 * In-app navigation guard for page-level editors (forms, surveys, campaigns,
 * certificate templates). The app has no router: every in-app move goes
 * through `RouteContext.navigate` (AppLink, the sidebar, buttons) or the
 * browser's back/forward (`popstate`). Both ask `shouldBlock()` first; while
 * an editor with unsaved changes is mounted (`useNavigationGuard(dirty)`),
 * the move waits for "Descartar alterações?" (the same words as the guarded
 * dialogs). Leaving the page or reloading gets the browser's own question.
 */

type Blocker = () => boolean
const blockers = new Set<Blocker>()
type Ask = (proceed: () => void) => void
let ask: Ask | null = null
/* While a confirmed move runs, the guards let it through. */
let bypass = false

/** True when some mounted editor has unsaved changes and the prompt took over the move. */
export const interceptNavigation = (proceed: () => void): boolean => {
  if (bypass || !ask || ![...blockers].some((b) => b())) return false
  ask(() => {
    bypass = true
    try { proceed() } finally { bypass = false }
  })
  return true
}

/** Registers the page as dirty-aware: in-app moves and unloads ask first while `dirty`. */
export const useNavigationGuard = (dirty: boolean) => {
  const dirtyRef = React.useRef(dirty)
  dirtyRef.current = dirty
  React.useEffect(() => {
    const blocker: Blocker = () => dirtyRef.current
    blockers.add(blocker)
    return () => { blockers.delete(blocker) }
  }, [])
  React.useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])
}

/** The prompt, mounted once by the route provider. */
export const NavigationGuardPrompt = () => {
  const { t } = useTranslation('common')
  const [pending, setPending] = React.useState<(() => void) | null>(null)
  const keepRef = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    ask = (proceed) => setPending(() => proceed)
    return () => { ask = null }
  }, [])
  return (
    <DialogPrimitive.Root open={pending !== null} onOpenChange={(next) => { if (!next) setPending(null) }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/40" />
        <DialogPrimitive.Content
          role="alertdialog"
          data-testid="navigation-guard"
          onOpenAutoFocus={(e) => { e.preventDefault(); keepRef.current?.focus() }}
          className="fixed left-1/2 top-1/2 z-[60] w-[calc(100%-1.5rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-white p-5 shadow-xl"
        >
          <DialogPrimitive.Title className="text-base font-semibold text-gray-900">{t('unsavedGuard.title')}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-1.5 text-sm text-gray-600">{t('unsavedGuard.description')}</DialogPrimitive.Description>
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" ref={keepRef} data-testid="navigation-guard-keep" onClick={() => setPending(null)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-200">
              {t('unsavedGuard.keep')}
            </button>
            <button type="button" data-testid="navigation-guard-discard"
              onClick={() => { const proceed = pending; setPending(null); window.setTimeout(() => proceed?.(), 0) }}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300">
              {t('unsavedGuard.discard')}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
